// T3-B PR 4-bis (2026-05-12):
//   占い文言プール管理 Sheet (言語タブ + N 候補 + is_active トグル + 採用中ゼロ防止 backend validation)。
//   roadmap v3 §7.2 案 β、多言語方針 Y (= 言語ごと独立) を実装。
//
// Spec: design/sponsored-motif-spec.md v3 §6.8
//       design/motif-future-roadmap-20260511.md v3 §7.2 / §7.4

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Textarea } from '@/components/common/Textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/common/Tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { Switch } from '@/components/common/Switch';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Trash2, Plus, Sparkles } from 'lucide-react';

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    templateMotif: any | null
}

// 言語タブ (= 採用中の主要 5 言語、必要なら設定で増減)
const LANGUAGES = [
    { code: 'ja', label: '日本語' },
    { code: 'en', label: 'English' },
    { code: 'zh-CN', label: '简体中文' },
    { code: 'zh-TW', label: '繁體中文' },
    { code: 'ko', label: '한국어' }
];

export default observer(function OmikujiTextsPoolSheet({ open, onOpenChange, templateMotif }: Props) {
    const {
        omikujiTextStore: {
            templateMotifTexts,
            listByTemplateMotif,
            createByTemplateMotif,
            update: updateText,
            remove: removeText,
            generate: generateTexts
        },
        modalStore: { showAlertModal }
    } = useStore();

    const [language, setLanguage] = useState<string>('ja');
    const [newBody, setNewBody] = useState('');
    const [newFortune, setNewFortune] = useState<string>('');
    const [genCount, setGenCount] = useState<string>('5');
    const [busy, setBusy] = useState(false);

    const reload = () => {
        if (!templateMotif) return;
        return flowResult(listByTemplateMotif({ template_motif_id: templateMotif.id }));
    };

    useEffect(() => {
        if (!open) return;
        setLanguage('ja');
        setNewBody('');
        setNewFortune('');
        reload();
    }, [open, templateMotif?.id]);

    if (!templateMotif) return null;

    const defaultFortune = templateMotif.default_tier_value ?? 1;

    // 言語別の候補リスト (= active 候補は是非 1 件以上残るよう backend が validate)
    const langItems = templateMotifTexts.filter(t => t.language === language);

    const handleToggleActive = async (item: any, nextActive: boolean) => {
        setBusy(true);
        try {
            const res = await flowResult(updateText({ id: item.id, data: { is_active: nextActive } }));
            if (res?.ok) {
                toastify('success', nextActive ? '採用に戻しました' : '採用解除しました');
                reload();
            } else {
                toastify('error', res?.message || '更新に失敗 (= 採用中ゼロ防止に該当?)');
            }
        } finally {
            setBusy(false);
        }
    };

    const handleEditBody = async (item: any, body: string) => {
        if (!body.trim() || body === item.body) return;
        setBusy(true);
        try {
            const res = await flowResult(updateText({ id: item.id, data: { body } }));
            if (res?.ok) {
                toastify('success', '更新しました');
                reload();
            } else {
                toastify('error', res?.message || '更新に失敗');
            }
        } finally {
            setBusy(false);
        }
    };

    const handleRemove = (item: any) => {
        showAlertModal({
            title: '削除確認',
            size: 'sm',
            content: `「${item.body.slice(0, 30)}...」を削除しますか?\n採用中の最後の候補は backend で削除拒否されます。`,
            onSave: async () => {
                setBusy(true);
                try {
                    const res = await flowResult(removeText(item.id));
                    if (res?.ok) {
                        toastify('success', '削除しました');
                        reload();
                    } else {
                        toastify('error', res?.message || '削除に失敗');
                    }
                } finally {
                    setBusy(false);
                }
            }
        });
    };

    const handleManualCreate = async () => {
        if (!newBody.trim() || !newFortune) {
            toastify('error', '占い文と fortune を入力してください');
            return;
        }
        setBusy(true);
        try {
            const res = await flowResult(createByTemplateMotif({
                template_motif_id: templateMotif.id,
                data: {
                    fortune: Number(newFortune),
                    language,
                    body: newBody,
                    is_active: true,
                    priority: 1
                }
            }));
            if (res?.ok) {
                toastify('success', '追加しました');
                setNewBody('');
                reload();
            } else {
                toastify('error', res?.message || '追加に失敗');
            }
        } finally {
            setBusy(false);
        }
    };

    const handleGenerate = async () => {
        const count = Number(genCount);
        if (!count || count < 1) {
            toastify('error', '生成件数を入力してください');
            return;
        }
        setBusy(true);
        try {
            const res = await flowResult(generateTexts({
                fortune: defaultFortune,
                motif: templateMotif.slug,
                language,
                count
            }));
            if (res?.ok) {
                toastify('success', `${count} 件生成リクエスト完了`);
                reload();
            } else {
                toastify('error', res?.message || '生成に失敗');
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[720px] sm:max-w-[720px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>
                        占い文言プール:
                        {templateMotif.name}
                        {' '}
                        (
                        {templateMotif.slug}
                        )
                    </SheetTitle>
                    <SheetDescription>
                        言語別 N 候補 + is_active トグル (= 採用中ゼロ防止 validation あり)。
                        default tier value =
                        {' '}
                        {defaultFortune}
                    </SheetDescription>
                </SheetHeader>

                <div className='py-4'>
                    <Tabs value={language} onValueChange={setLanguage}>
                        <TabsList className='grid grid-cols-5 w-full'>
                            {LANGUAGES.map(l => (
                                <TabsTrigger key={l.code} value={l.code}>{l.label}</TabsTrigger>
                            ))}
                        </TabsList>

                        {LANGUAGES.map(l => (
                            <TabsContent key={l.code} value={l.code} className='space-y-4 pt-4'>
                                {/* 生成 (= Claude 一括) */}
                                <div className='flex items-end gap-2 p-3 bg-gray-50 rounded'>
                                    <div className='flex-1'>
                                        <Label className='text-xs'>Claude 自動生成</Label>
                                        <p className='text-xs text-gray-500'>
                                            fortune=
                                            {defaultFortune}
                                            , motif=
                                            {templateMotif.slug}
                                            , language=
                                            {l.code}
                                            {' '}
                                            の候補を一括追加
                                        </p>
                                    </div>
                                    <div className='w-20'>
                                        <Label className='text-xs'>件数</Label>
                                        <Input type='number' value={genCount} onChange={e => setGenCount(e.target.value)} min={1} max={50} />
                                    </div>
                                    <Button variant='outline' size='sm' onClick={handleGenerate} disabled={busy}>
                                        <Sparkles className='w-4 h-4 mr-1' />
                                        生成
                                    </Button>
                                </div>

                                {/* 手動追加 */}
                                <div className='border border-gray-200 rounded p-3 space-y-2'>
                                    <Label className='text-xs'>手動追加</Label>
                                    <div className='flex gap-2'>
                                        <Select value={newFortune} onValueChange={setNewFortune}>
                                            <SelectTrigger className='w-32'>
                                                <SelectValue placeholder='fortune' />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='1'>1: 大吉</SelectItem>
                                                <SelectItem value='2'>2: 中吉</SelectItem>
                                                <SelectItem value='3'>3: 小吉</SelectItem>
                                                <SelectItem value='4'>4: 末吉</SelectItem>
                                                <SelectItem value='5'>5: 凶</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Input
                                            value={newBody}
                                            onChange={e => setNewBody(e.target.value)}
                                            placeholder='占い文 (200 字以内)'
                                            className='flex-1'
                                        />
                                        <Button variant='outline' size='sm' onClick={handleManualCreate} disabled={busy}>
                                            <Plus className='w-4 h-4 mr-1' />
                                            追加
                                        </Button>
                                    </div>
                                </div>

                                {/* 候補一覧 */}
                                <div>
                                    <Label className='text-xs'>
                                        登録候補 (
                                        {langItems.length}
                                        {' '}
                                        件)
                                    </Label>
                                    {langItems.length === 0 ?
                                        (
                                            <div className='py-8 text-center text-gray-500 text-sm'>
                                                {l.label}
                                                {' '}
                                                の候補がありません
                                            </div>
                                        ) :
                                        (
                                            <div className='space-y-2 mt-2'>
                                                {langItems.map(item => (
                                                    <div
                                                        key={item.id}
                                                        className={`flex items-start gap-2 p-2 rounded border ${item.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'}`}
                                                    >
                                                        <div className='text-xs text-gray-500 w-20 pt-1'>
                                                            fortune
                                                            {' '}
                                                            {item.fortune}
                                                        </div>
                                                        <Textarea
                                                            defaultValue={item.body}
                                                            onBlur={e => handleEditBody(item, e.target.value)}
                                                            rows={2}
                                                            className='flex-1 text-sm'
                                                        />
                                                        <div className='flex flex-col items-center gap-1'>
                                                            <Switch
                                                                checked={item.is_active}
                                                                onCheckedChange={(v: boolean) => handleToggleActive(item, v)}
                                                                disabled={busy}
                                                            />
                                                            <span className='text-[10px] text-gray-500'>採用</span>
                                                        </div>
                                                        <Button
                                                            variant='outline'
                                                            size='sm'
                                                            onClick={() => handleRemove(item)}
                                                            className='text-red-600'
                                                            disabled={busy}
                                                        >
                                                            <Trash2 className='w-4 h-4' />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                </div>
                            </TabsContent>
                        ))}
                    </Tabs>
                </div>
            </SheetContent>
        </Sheet>
    );
});
